import {
  createContext,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
} from 'react';
import { CHAVE_TEMA, salvarTema, temaSalvo, type Tema } from '@/lib/theme';

export type PlatformTheme = Tema;
export const PLATFORM_THEME_KEY = CHAVE_TEMA;
export interface PlatformThemeValue {
  theme: PlatformTheme;
  setTheme: Dispatch<SetStateAction<PlatformTheme>>;
  toggleTheme: () => void;
}
export const PlatformThemeContext = createContext<PlatformThemeValue | null>(
  null,
);

/** One preference for auth, member pages, admin and portals. Route changes never reset it. */
export function PlatformThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setTheme] = useState<PlatformTheme>(temaSalvo);
  useLayoutEffect(() => {
    salvarTema(theme);
  }, [theme]);
  useEffect(() => {
    const sync = (event: StorageEvent) => {
      if (event.key === PLATFORM_THEME_KEY || event.key === null) {
        setTheme(event.newValue === 'light' ? 'light' : 'dark');
      }
    };
    window.addEventListener('storage', sync);
    return () => window.removeEventListener('storage', sync);
  }, []);
  const toggleTheme = useCallback(() => setTheme(current => current === 'dark' ? 'light' : 'dark'), []);
  const value = useMemo(
    () => ({ theme, setTheme, toggleTheme }),
    [theme, toggleTheme],
  );
  return (
    <PlatformThemeContext.Provider value={value}>
      {children}
    </PlatformThemeContext.Provider>
  );
}
